/*
  A class to parse a script
*/

/* eslint-disable no-ex-assign */

import moo from 'moo'
import Module from './module.js'
import { filterSuggestions } from './filter.js'

// The lexer, to turns the script into easy-processed tokens
const LEXER = moo.states({
  /* 21 classes
    number
    unknown_number
    string_begin
    literal
    prefix
    identifier
    unknown_identifier
    special
    assign
    binary
    mixed
    unary
    unknown_operator
    block
    control
    delete
    comment
    group
    space
    newline
    unknown_char
  */
  main: {
    // an invalid identifier made of valid characters
    unknown_identifier: /[0-9]+[a-zA-Z_][a-zA-Z_0-9]*/,
    // a number
    number: /[0-9]+(?:\.[0-9]*)?|\.[0-9]+(?!\w)/,
    // un unknown mixed of digits and dot
    unknown_number: /[0-9][.0-9]*|\.+[0-9][.0-9]*(?!\w)/,
    // the prefix of a variable
    prefix: ['$$', '$', '@@', '@'],
    // special operators, with specific befoviors
    special: ['.', ',', ':', '?', ';'],
    // other operators, ordered by sixe
    assign3: { match: ['//=', '**='], type: () => 'assign' },
    binary2: { match: ['//', '**', '&&', '||', '==', '!=', '<=', '>='], type: () => 'binary' },
    assign2: { match: ['=', '+=', '-=', '*=', '/=', '%='], type: () => 'assign' },
    binary1: { match: ['+', '*', '/', '%', '<', '>'], type: () => 'binary' },
    mixed1: { match: ['-'], type: () => 'mixed' },
    unary1: { match: ['!'], type: () => 'unary' },
    // notbinary: { match: /not +(?:in|of)(?![\w\d])|is +not(?![\w\d])/,
    //   type: () => 'binary',
    //   value: v => v.replace(/ +/, ' ') },
    // a valid indentifier
    identifier: /[a-zA-Z_][a-zA-Z_0-9]*/,
    // identifier: {
    //   match: /[a-zA-Z_][a-zA-Z_0-9]*/,
    //   type: moo.keywords({
    //     // block declarators
    //     block: ['if', 'for', 'else'],
    //     // function declarators
    //     function: ['function', 'fun'],
    //     // other operators, ordered by sixe
    //     unary: ['not', 'typeof'],
    //     binary: ['and', 'or', 'of', 'in', 'is'],
    //     // specific flow operators
    //     control: ['break', 'continue', 'return', 'pass'],
    //     // delete a field
    //     delete: ['delete'],
    //     // literals with value
    //     literal: ['true', 'false', 'null', 'Infinity'],
    //   }),
    // },
    comment: /#[^\n]*/,
    // a invalid group of characters that could have been an operator
    unknown_operator: /[-!%&*+=<>$@?/:|~]+/,
    // group with begin and end
    group: ['(', '[', '{', ')', ']', '}'],
    // strings
    simple_string_begin: { match: "'", next: 'simple_string', type: () => 'string_begin' },
    double_string_begin: { match: '"', next: 'double_string', type: () => 'string_begin' },
    // ignorable spaces
    space: /[ \t]+/,
    ignore_newline: { match: /\\[ \t]*\n/, lineBreaks: true, type: () => 'space' },
    // hard new line
    newline: { match: '\n', lineBreaks: true },
    // invalid character
    unknown_char: /./,
  },
  simple_string: {
    string: /(?:[^\n'\\]|\\[^\n])+/,
    string_end: { match: "'", next: 'main' },
    ignore_newline: { match: /\\[ \t]*\n/, lineBreaks: true },
    expected_string_end: { match: /\n/, lineBreaks: true, next: 'main' },
  },
  double_string: {
    string: /(?:[^\n"\\]|\\[^\n])+/,
    string_end: { match: '"', next: 'main' },
    ignore_newline: { match: /\\[ \t]*\n/, lineBreaks: true },
    expected_string_end: { match: /\n/, lineBreaks: true, next: 'main' },
  },
})

const IDENTIFIERS = {
  'if': 'block',
  'for': 'block',
  'else': 'block',

  'fun': 'function',
  'function': 'function',

  'not': 'unary',
  'typeof': 'unary',

  'and': 'binary',
  'or': 'binary',
  'of': 'binary',
  'in': 'binary',
  'is': 'binary',

  'not of': 'binary',
  'not in': 'binary',
  'is not': 'binary',

  'break': 'control',
  'continue': 'control',
  'return': 'control',
  'pass': 'control',

  'del': 'delete',
  'delete': 'delete',

  'true': 'literal',
  'false': 'literal',
  'null': 'literal',
  'Infinity': 'literal',
}

const REPR = function (value) {
  /* nice display of value for errors */
  if (typeof value === 'number') return value + ''
  return JSON.stringify(value)
}

export class ParserError extends Error {
  /* The error base class
  codestack is filled with the error stack */
  constructor (message, position) {
    super(message)
    this.codestack = []
    if (position) this.codestack.push(position)
  }
}
export class ParseRuntimeError extends ParserError {
  /* For runtime error, should not happends, but bugs ... */
  constructor (error, position) {
    super(error.name + ': ' + error.message, position)
    this.error = error
  }
}

export class ParserTypeError extends ParserError {}
export class ParserArithmeticError extends ParserError {}
export class ParserReferenceError extends ParserError {}

export class OperatorTypeError extends ParserTypeError {
  /* Nicer error for wrong types with an operator */
  constructor (operator, operande1, operande2) {
    if (operande2 === undefined) {
      super([
        'no "',
        operator,
        '" operator for type "',
        TYPEOF(operande1),
        '"',
      ].join(''))
    } else {
      super([
        'no "',
        operator,
        '" operator for type "',
        TYPEOF(operande1),
        '" and "',
        TYPEOF(operande2),
        '"',
      ].join(''))
    }
  }
}
export class OperatorArithmeticError extends ParserArithmeticError {
  /* Nicer error for arithmeric error on operator */
  constructor (operator, operande1, operande2) {
    if (operande2 === undefined) {
      super([
        'illegal operation `',
        operator,
        ' ',
        REPR(operande1),
        '`',
      ].join(''))
    } else {
      super([
        'illegal operation `',
        REPR(operande1),
        ' ',
        operator,
        ' ',
        REPR(operande2),
        '`',
      ].join(''))
    }
  }
}

// list of variables prefix, with their respective field
const PREFIX = {
  '$': 'context',
  '$$': 'contexts',
  '@': 'local',
  '@@': 'script',
  '': 'global',
}
// the status of each type of varibale:
// * readonly: cannot be assigned of called
// * callable: can be called but not assigned,
//      cannot reference unfinished path
// * editable: can be assigned but not called
// * freevar: can be assigned, base variables
//      can be non JSON values (functions or Infinity)
const VAR_STATUS = {
  'global': 'callable',
  'script': 'freevar',
  'local': 'editable',
  'context': 'readonly',
  'contexts': 'readonly',
}
// the value of literals (case insensitive)
const LITERAL_VALUE = {
  'true': true,
  'false': false,
  'null': null,
  // Infinity can be useful for corner cases, but cannot be used as final value
  'Infinity': Infinity,
}
// the type of literals (case insensitive)
const LITERAL_CAST = {
  'true': 'boolean',
  'false': 'boolean',
  'null': 'null',
  'Infinity': 'number',
}
// the closing character of groups
const CLOSING = {
  '(': ')',
  '[': ']',
  '{': '}',
}
// the priority of each binary operator
const PRIORITY = {
  'of': 11,
  'in': 11,
  '**': 10,
  '*': 9,
  '/': 9,
  '%': 9,
  '//': 9,
  '+': 7,
  '-': 7,
  '==': 6,
  '!=': 6,
  '<': 6,
  '>': 6,
  '<=': 6,
  '>=': 6,
  'is': 6,
  '&&': 5,
  '||': 5,
  'and': 5,
  'or': 5,
}

const PRECISION = 12
const EPSILON = 0.1 ** PRECISION
// the base types, ie not compounded
const BASE_TYPES = { 'number': true, 'boolean': true, 'string': true, 'null': true }
// the types interpreted as numbers
const NUM_TYPES = { 'number': true, 'boolean': true }

// returns the type of variable, including null
const TYPEOF = function (value) {
  if (value === null || value === undefined) return 'null'
  else if (Array.isArray(value)) return 'array'
  else if (value instanceof Module) return 'module'
  else return typeof value
}
// tells if a value is interpreted as true
const TRUE = function (value) {
  if (typeof value === 'object') {
    if (value === null) return false
    else if (Array.isArray(value)) return value.length
    for (let name in value) return true
    return false
  } else if (typeof value === 'number') {
    return Math.abs(value) > EPSILON
  } else return value
}
// change the values to more easily comparable types
const REDUCE = function (value) {
  if (value === undefined) return null
  else if (typeof value === 'boolean') return value ? 1 : 0
  else return value
}
const NUMBER_TO_STRING = function (value) {
  return value.toPrecision(PRECISION).replace(/\.?0*$/, '')
}
const TO_STRING = function (value) {
  let type = TYPEOF(value)
  if (type === 'string') return value
  else if (type === 'number') return NUMBER_TO_STRING(value)
  else if (BASE_TYPES[type]) return value + ''
  // else return null
  else {
    throw new ParserTypeError('type "' + type +
    '" cannot be converted to string')
  }
}
const TO_KEY = function (value) {
  let type = typeof value
  if (type === 'number') value = NUMBER_TO_STRING(value)
  else if (type === 'boolean') return value ? 1 : 0
  else if (BASE_TYPES[type]) return null
  else {
    throw new ParserTypeError('type "' + type +
    '" cannot be converted to key')
  }
  if (!value.match(/[-+]?^\d+$/)) return null
  return parseInt(value)
}
// a soft equal, with deep comparison of arrays and objects
const EQUALS = function (a, b) {
  a = REDUCE(a)
  b = REDUCE(b)
  if (a === b) return true
  else if (typeof a !== typeof b) return false
  else if (typeof a === 'object') {
    if (a === null || b === null) return false
    let array = Array.isArray(a)
    if (array !== Array.isArray(b)) return false
    else if (array) {
      let length = a.length
      if (length !== b.length) return false
      for (let i = 0; i < length; i++) {
        if (!EQUALS(a[i], b[i])) return false
      }
    } else if (a instanceof Module || b instanceof Module) {
      return false
    } else {
      for (let name in a) {
        if (!(name in b)) return false
        if (!EQUALS(a[name], b[name])) return false
      }
      for (let name in b) {
        if (!(name in a)) return false
      }
    }
    return true
  } else if (typeof a === 'number') {
    return a === b || Math.abs(a - b) <= EPSILON
  } else return a === b
}

const COMPARE = function (num, other) {
  return (a, b) => {
    a = REDUCE(a)
    b = REDUCE(b)
    if (NUM_TYPES[typeof a] && NUM_TYPES[typeof b]) return num(a, b)
    else return other(a, b)
  }
}
// list of all binary operators
const BINARY = {
  // number addition and string concatenation
  '+': (a, b) => {
    let typea = TYPEOF(a), typeb = TYPEOF(b)
    // number addition
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a + b
      // check for NaN, in `-Infinity + Infinity` for instance
      if (isNaN(value)) throw new OperatorArithmeticError('+', a, b)
      return value
    // string concatenation
    } else if (typea === 'string') {
      if (BASE_TYPES[typeb]) return a + TO_STRING(b)
    } else if (typeb === 'string') {
      if (BASE_TYPES[typea]) return TO_STRING(a) + b
    }
    throw new OperatorTypeError('+', a, b)
  },
  // number soustraction
  '-': (a, b) => {
    let typea = typeof a, typeb = typeof b
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a - b
      // check for NaN, in `Infinity - Infinity` for instance
      if (isNaN(value)) throw new OperatorArithmeticError('-', a, b)
      return value
    }
    throw new OperatorTypeError('-', a, b)
  },
  // number multiplication and string repetition
  '*': (a, b) => {
    let typea = typeof a, typeb = typeof b
    // number multiplication
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a * b
      // check for NaN, in `0 * Infinity` for instance
      if (isNaN(value)) throw new OperatorArithmeticError('*', a, b)
      return value
    }
    let string = null, num
    // string repetition
    if (typea === 'string') {
      if (NUM_TYPES[typeb]) {
        string = a
        num = b
      }
    } else if (typeb === 'string') {
      if (NUM_TYPES[typea]) {
        string = b
        num = a
      }
    }
    if (string !== null) {
      // check for float number (only bool or int allowed)
      if (typeof num === 'boolean') return num ? string : ''
      else if (typeof num !== 'number' || parseInt(num) !== num) {
        throw new OperatorArithmeticError('*', a, b)
      }
      let acc = []
      while (num-- > 0) acc.push(string)
      return acc.join('')
    }
    throw new OperatorTypeError('*', a, b)
  },
  // number division
  '/': (a, b) => {
    let typea = typeof a, typeb = typeof b
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a / b
      // check for 0 division, which could result in undesired Infinity
      // and NaN, like `Infinity / Infinity`
      if (!b || isNaN(value)) throw new OperatorArithmeticError('/', a, b)
      return value
    }
    throw new OperatorTypeError('/', a, b)
  },
  // number modulo
  '%': (a, b) => {
    let typea = typeof a, typeb = typeof b
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a % b
      // check for NaN, in `1 % 0` for instance
      if (isNaN(value)) throw new OperatorArithmeticError('%', a, b)
      return value
    }
    throw new OperatorTypeError('%', a, b)
  },
  // number power
  '**': (a, b) => {
    let typea = typeof a, typeb = typeof b
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = Math.pow(a, b)
      // check for NaN, in `-1 ** 0.5` for instance
      if (isNaN(value)) throw new OperatorArithmeticError('**', a, b)
      return value
    }
    throw new OperatorTypeError('**', a, b)
  },
  // number division with integer result
  '//': (a, b) => {
    let typea = typeof a, typeb = typeof b
    if (NUM_TYPES[typea] && NUM_TYPES[typeb]) {
      let value = a / b
      // check for 0 division, which could result in undesired Infinity
      // and NaN, like `Infinity // Infinity`
      if (!b || isNaN(value)) throw new OperatorArithmeticError('//', a, b)
      return Math.sign(value) * Math.floor(Math.abs(value) + EPSILON)
    }
    throw new OperatorTypeError('//', a, b)
  },
  // logical operators
  '&&': (a, b) => TRUE(a) ? b : a,
  '||': (a, b) => TRUE(a) ? a : b,
  'and': (a, b) => TRUE(a) ? b : a,
  'or': (a, b) => TRUE(a) ? a : b,
  // soft equal of deep comparison
  '==': (a, b) => EQUALS(a, b),
  '!=': (a, b) => !EQUALS(a, b),
  // comparison
  '<': COMPARE((a, b) => a < b - EPSILON, (a, b) => a < b),
  '>': COMPARE((a, b) => a > b + EPSILON, (a, b) => a > b),
  '<=': COMPARE((a, b) => a <= b + EPSILON, (a, b) => a <= b),
  '>=': COMPARE((a, b) => a >= b - EPSILON, (a, b) => a >= b),
  // strict equal
  'is': (a, b) => a === b ||
    typeof a === 'number' && typeof b === 'number' &&
      Math.abs(a - b) <= EPSILON,
  'is not': (a, b) => !(a === b ||
    typeof a === 'number' && typeof b === 'number' &&
      Math.abs(a - b) <= EPSILON),
  // key in object / array
  'in': (a, b) => {
    if (TYPEOF(b) === 'array') {
      a = TO_KEY(a)
      return a !== null && a >= 0 && a < b.length
    } else if (TYPEOF(b) === 'object') {
      return b.hasOwnProperty(TO_STRING(a))
    } else return false
  },
  // value in object / array
  'of': (a, b) => {
    if (TYPEOF(b) === 'array') {
      for (let c of b) {
        if (BINARY['is'](a, c)) return true
      }
    } else if (TYPEOF(b) === 'object') {
      for (let c in b) {
        if (BINARY['is'](a, b[c])) return true
      }
    }
    return false
  },
}
// operators that can be chained: 1 < 2 == 2 < 2
const CHAINABLE = {
  '==': true,
  '!=': true,
  '<': true,
  '>': true,
  '<=': true,
  '>=': true,
  'is': true,
}
// merging not with other operators
const NOTOP = (op, notop) => {
  BINARY[notop] = BINARY[notop] || ((binop) => {
    return (a, b) => !binop(a, b)
  })(BINARY[op])
  PRIORITY[notop] = PRIORITY[op]
  if (CHAINABLE[op]) CHAINABLE[notop] = true
}
NOTOP('is', 'is not')
NOTOP('in', 'not in')
NOTOP('of', 'not of')
// lazy version of logical operators
const LAZY_BINARY = {
  '&&': (a, b, variables) => TRUE(a) ? b.resolve(variables) : a,
  '||': (a, b, variables) => TRUE(a) ? a : b.resolve(variables),
  'and': (a, b, variables) => TRUE(a) ? b.resolve(variables) : a,
  'or': (a, b, variables) => TRUE(a) ? a : b.resolve(variables),
}
// unary operators (always have highest priority)
const UNARY = {
  // number negative
  '-': a => {
    let typea = typeof a
    if (NUM_TYPES[typea]) return -a
    throw new OperatorTypeError('-', a)
  },
  // logical not
  '!': a => !TRUE(a),
  'not': a => !TRUE(a),
  'typeof': a => TYPEOF(a),
}
// assignation operators (always have lowest priority)
// the function is the value to store
const ASSIGN = {
  '=': (_, a) => a,
  '+=': BINARY['+'],
  '-=': BINARY['-'],
  '*=': BINARY['*'],
  '/=': BINARY['/'],
  '%=': BINARY['%'],
  '**=': BINARY['**'],
  '//=': BINARY['//'],
  '&&=': BINARY['&&'],
  '||=': BINARY['||'],
}
// lazy version of logical assignation operators
const LAZY_ASSIGN = {
  '&&=': LAZY_BINARY['&&'],
  '||=': LAZY_BINARY['||'],
}

export default class Parser {
  constructor (code, prefix, status, variables, prototype) {
    this.prefix = prefix || PREFIX
    this.status = status ? { ...VAR_STATUS, ...status } : VAR_STATUS
    this.variables = {
      global: Module.modules,
      script: {},
      ...variables || {},
    }
    this.prototype = prototype || Module.prototypes
    this.tokens = []
    this.index = -1
    let token, lexer = LEXER
    lexer.reset(code)
    // eslint-disable-next-line no-cond-assign
    while (token = lexer.next()) this.tokens.push(token)
    this.syntax = []
    this.newline = false
    this.next()
  }
  // parses a program
  parseProgram () {
    // console.log('parseProgram')
    let commands = [], error = false, end_error = null
    while (true) {
      this.passSpaces()
      // end of program
      if (!this.token) {
        break
      // only character that can stop parseCommand
      } else if (this.token.text === '}') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['group', 'codeblock'],
          error: 'unexpected character',
        })
        error = true
        end_error = null
        this.next()
      // parse the commands
      } else {
        let command = this.parseCommand(false, false, false, end_error)
        // add command
        if (command) {
          commands.push(command)
          end_error = null
        // command wasn't finished
        } else {
          error = true
          end_error = 'unfinished command'
        }
        // newline was part of the command, and is not related to the next one
        this.newline = false
      }
    }
    if (end_error && this.syntax.length &&
      !this.syntax[this.syntax.length - 1].error) {
      this.syntax[this.syntax.length - 1].error = end_error
    }
    return this.createCommands(commands, error, true)
  }
  // parses a program that only returns a value
  parseValueProgram (empty) {
    // console.log('parseProgram')
    let values = [], error = false, syntax, errorval
    while (true) {
      // end without value before, feed with empty
      this.passSpaces()
      if (!this.token) {
        if (empty) values.push(empty)
        else {
          error = true
          if (syntax && !syntax.error) {
            syntax.error = 'item expected after'
          }
        }
        break
      // a comma without value before, feed with empty
      } else if (this.token.text === ',') {
        syntax = {
          ...this.position(this.token),
          type: ['separator', 'comma'],
        }
        this.syntax.push(syntax)
        this.next()
        this.passSpaces()
        if (empty) {
          values.push(empty)
          empty = null
        } else {
          error = true
          syntax.error = 'item expected before'
        }
      // probably a value
      } else {
        let length = this.syntax.length
        let value = this.parseValue(null, null, errorval)
        errorval = null
        // nothing was parsed, unexpected character
        if (this.syntax.length === length) {
          error = true
          this.parseUnexpected()
          continue
        } else if (!value || value.error) error = true
        else values.push(value)
        this.passSpaces()
        // end of the values
        if (!this.token) break
        // comma
        else if (this.token.text === ',') {
          syntax = {
            ...this.position(this.token),
            type: ['separator', 'comma'],
          }
          this.syntax.push(syntax)
          this.next()
        // deals with whatever it is next turn
        } else errorval = 'unexpected value'
      }
    }
    let out = { type: 'value', error: error }
    if (!error) {
      out.resolve = (function (values) {
        return function (variables) {
          variables = {
            global: Module.modules,
            script: {},
            ...variables,
          }
          for (let value of values) {
            if (value.resolve) value = value.resolve(variables)
            else value = value.value
            if (value !== null && value !== undefined) return value
          }
          return null
        }
      })(values)
      out.condition = function (variables) {
        return TRUE(this.resolve(variables))
      }
      out.debug = {
        resolve: 'program_value',
        values: values,
      }
    }
    return out
  }
  // gives the position object to insert in syntax
  position (token, length) {
    if (length === undefined) length = token.text.length
    else if (typeof length === 'string') length = length.length
    return {
      offset: token.offset,
      length: length,
      line: token.line,
      col: token.col,
    }
  }
  // parses the next token
  next () {
    // reset newline
    this.newline = false
    this.index++
    this.token = this.tokens[this.index]
    // console.log('next', this.token)
  }

  identify () {
    if (this.token && this.token.type === 'identifier') {
      if (this.tokens.length > this.index + 2 &&
        this.tokens[this.index + 1].type === 'space' &&
        this.tokens[this.index + 2].type === 'identifier') {
        let text = this.token.text + ' ' + this.tokens[this.index + 2].text
        if (text in IDENTIFIERS) {
          this.token.text = text
          this.token.type = IDENTIFIERS[text]
          this.index += 2
          return this.token
        }
      }
      this.token.type = IDENTIFIERS[this.token.text] || 'identifier'
    }
    return this.token
  }
  // skip paces
  passSpaces () {
    // console.log('passSpaces')
    let newline = this.newline, space = null, length = 0
    while (this.token) {
      // skip comments too
      if (this.token.type === 'comment') {
        if (space) {
          this.syntax.push({
            ...this.position(space, length),
            type: ['space'],
          })
          space = null
        }
        this.syntax.push({
          ...this.position(this.token),
          type: ['comment'],
        })
        this.next()
        continue
      // skip newlines
      } else if (this.token.type === 'newline') {
        newline = true
      // skip spaces
      } else if (this.token.type !== 'space') break
      if (!space) {
        space = this.token
        length = 0
      }
      length += this.token.text.length
      this.next()
    }
    if (space) {
      this.syntax.push({
        ...this.position(space, length),
        type: ['space'],
      })
    }
    // save if a newline was encountered
    this.newline = newline
  }
  // parse one signle command
  parseCommand (inLoop, inFunction, inline, error) {
    // console.log('parseCommand', JSON.stringify(this.token))
    if (!this.token) return null
    this.identify()
    let command
    // nothing in this command, return pass to makes one-line if happy
    if (!inline && this.token.text === ';') {
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator', 'semicolon'],
        error: error,
      })
      this.next()
      return { type: 'command', control: 'pass', error: !!error }
    // a control command (pass, break, return, ...)
    } else if (this.token.type === 'control') {
      command = this.parseControl(inLoop, inFunction, error)
    //  a block command (if, for)
    } else if (this.token.type === 'block') {
      command = this.parseBlock(inLoop, inFunction, error)
    //  a delete command
    } else if (this.token.type === 'delete') {
      command = this.parseDelete(error)
    } else {
      let length = this.syntax.length
      // parse left part first, it could be a variable to assign
      command = this.parseLeftValue(error)
      if (command) {
        this.passSpaces()
        // keep following the getters
        while (command && this.token &&
          (this.token.text === '.' || !this.newline && this.token.text === '[')) {
          command = this.parseGetter(command)
          this.passSpaces()
        }
        if (this.token && command && !this.newline &&
          this.token.text !== '}' && this.token.text !== ';') {
          // an assignation
          if (this.token.type === 'assign' && command.type === 'reference') {
            command = this.parseAssign(command)
          // just a value
          } else {
            command = this.parseValue(0, command)
          }
        }
      // nothing ot parse, parse one unexpected token to continue
      } else if (length === this.syntax.length) {
        this.parseUnexpected()
      }
    }
    // end of the command
    if (inline || !this.token || this.newline || this.token.text === '}') {
      return command
    // also end of the command, but parse semicolon
    } else if (this.token.text === ';') {
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator', 'semicolon'],
      })
      this.next()
      return command
    // anything after is unexpected
    } else {
      return
      // this.parseUnexpected()
      // if(!command) return// { type: 'command', error: true }
      // commands.error = true
      // return command
    }
  }
  // parses a full block of commands surounded by {}
  parseCommandBlock (inLoop, inFunction, error) {
    // console.log('parseCommandBlock')
    let syntax = {
      ...this.position(this.token),
      type: ['group', 'codeblock'],
      error: error,
    }
    this.syntax.push(syntax)
    this.next()
    let commands = [], end_error = null
    error = !!error
    while (true) {
      this.passSpaces()
      // missing }
      if (!this.token) {
        syntax.error = 'no closing character'
        return
      // finished
      } else if (this.token.text === '}') {
        break
      // parse all commands
      } else {
        let command = this.parseCommand(inLoop, inFunction, false, end_error)
        // add command
        if (command) {
          commands.push(command)
          end_error = null
        // command wasn't finished
        } else {
          error = true
          end_error = 'unfinished command'
        }
        // newline was part of the command, and is not related to the next one
        this.newline = false
      }
    }
    this.syntax.push({
      ...this.position(this.token),
      type: ['group', 'codeblock'],
      error: end_error,
    })
    this.next()
    this.passSpaces()
    return this.createCommands(commands, error)
  }
  // create a commands object from a list of commands
  createCommands (commands, error, program) {
    // console.log('createCommands')
    error = !!error || commands.filter(c => c.error).length > 0
    // pass commands are useless
    commands = commands.filter(c => c.control !== 'pass')
    let out = {
      type: 'commands',
      error: error,
    }
    if (!error) {
      out.resolve = (function (commands) {
        return function (variables) {
          if (program) {
            variables = {
              global: Module.modules,
              script: {},
              ...variables,
            }
          }
          for (let command of commands) {
            if (command.type === 'command') {
              //  a control is encountered (continue / break / return), stop here
              if (command.control) {
                let out = { command: command.control }
                if (command.resolve) out.value = command.resolve(variables)
                else if ('value' in command.value) out.value = command.value
                return out
              // resolve the command and check for its control
              } else if (command.resolve) {
                let out = command.resolve(variables)
                if (out && out.control) return out
              }
            // resolve the value
            } else if (command.resolve) command.resolve(variables)
          }
          return {}
        }
      })(commands)
      out.debug = { commands: commands }
    }
    return out
  }
  // parses a value
  parseValue (priority, left, error) {
    priority = priority || 0
    // console.log('parseValue', priority, left)
    if (!this.token) return left
    // find the left operande
    if (!left) {
      left = this.parseLeftValue(error)
      this.passSpaces()
    }
    if (!left) return null
    // look for operators after the left operande
    while (true) {
      // console.log('parseValue', priority, 'loop')
      // finished
      if (!this.token) return left
      else if (this.newline) {
        // only operator that can be after a newlin
        if (this.token.text === '.') left = this.parseGetter(left)
        // else end of value
        else return left
      } else {
        this.identify()
        switch (this.token.type) {
        case 'special':
          // field getter
          if (this.token.text === '.') {
            left = this.parseGetter(left)
            break
          // ternary condition, no other operator after
          } else if (this.token.text === '?' && priority <= 0) {
            return this.parseTernary(left)
          // unknown special character, not part of the value
          } else return left
        // an operator, expected to be binary
        case 'assign':
        case 'unknown_operator':
        case 'binary':
        case 'mixed':
        case 'unary':
          // get the (supposed) priority of the operator
          let pri
          if (this.token.type === 'binary' || this.token.type === 'mixed') {
            pri = PRIORITY[this.token.text]
          // // small hack to have "not in" and "not of" at the right priority
          // } else if (this.token.text === 'not') pri = PRIORITY['not in']
          } else if (this.token.type === 'unknown_operator') pri = 1
          else if (this.token.type === 'unary') pri = 100
          else if (this.token.type === 'assign') pri = 1
          // only parse this operator if higher priority
          if (pri <= priority) return left
          left = this.parseBinary(left)
          break
        case 'group':
          switch (this.token.text) {
          // a parenthesis for a function call
          case '(':
            left = this.parseCall(left)
            break
          // a bracket for an item get
          case '[':
            left = this.parseGetter(left)
            break
          // not part of the value
          default:
            return left
          }
          break
          // more value, shouldn't be here
        case 'number':
        case 'unknown_number':
        case 'string_begin':
        case 'literal':
        case 'prefix':
        case 'identifier':
        case 'unknown_identifier':
          left.error = true
          // parse all values remaining
          this.parseValue(priority, null, 'unexpected value')
          return left
        // something else, probably not part of the value
        default:
          return left // TODO check all cases
        }
      }
      this.passSpaces()
    }
  }
  // parses the left operande
  parseLeftValue (error) {
    // console.log('parseLeftValue')
    this.identify()
    switch (this.token.type) {
    // a number
    case 'number':
    case 'unknown_number':
      return this.parseNumber(error)
    // a string
    case 'string_begin':
      return this.parseString(error)
    // a literal
    case 'literal':
      return this.parseLiteral(error)
    // a variable path
    case 'prefix':
    case 'identifier':
    case 'unknown_identifier':
      return this.parseVariable(null, error)
    // a supposibly unary operator
    case 'assign':
    case 'unknown_operator':
    case 'binary':
    case 'mixed':
    case 'unary':
      return this.parseUnary(error)
    // a function definition
    case 'function':
      return this.parseFunction(error)
    case 'group':
      switch (this.token.text) {
      // an array
      case '[': return this.parseArray(error)
      // an object
      case '{': return this.parseObject(error)
      // a value between parenthesis for priority
      case '(':
        let syntax = {
          ...this.position(this.token),
          type: ['group'],
          error: error,
        }
        this.syntax.push(syntax)
        this.next()
        this.passSpaces()
        // parse vlaue inside parenthesis
        let value = this.parseValue()
        // end parenthesis found
        if (this.token.text === ')') {
          this.syntax.push({
            ...this.position(this.token),
            type: ['group'],
          })
          this.next()
          if (!value) return { type: 'value', error: true }
          if (error) value.error = true
          // break the chain of operators
          delete value.chain
          return value
        // no end parenthesis
        } else {
          syntax.error = 'no closing parenthesis'
          return
        }
      // end of group, not part of the value
      default: return
      }
    // not part of the left value
    default: return
    }
  }
  // parses a object definition
  parseObject (error) {
    // console.log('parseObject')
    let syntax = {
      ...this.position(this.token),
      type: ['group', 'object'],
      error: error,
    }
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    let items = [], seen = {}
    error = !!error
    while (true) {
      // reset newline for the case of next token is ":"
      this.newline = false
      // unclosed object
      if (!this.token) {
        syntax.error = 'no closing character'
        return
      // end of the object
      } else if (this.token.text === '}') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['group', 'object'],
        })
        this.next()
        break
      // a comma without any key or value before
      } else if (this.token.text === ',') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['separator', 'comma'],
          error: 'item expected',
        })
        this.next()
        this.passSpaces()
        continue
      }
      // look for a key
      // value first contains the key as it was a left operande
      let key, value = { type: 'value', error: true }
      let length = this.syntax.length, position
      switch (this.token.type) {
      // an identifier key
      case 'identifier':
      case 'unknown_identifier':
        // eslint-disable-next-line no-redeclare
        var unknown = this.token.type === 'unknown_identifier'
        if (unknown) error = true
        this.syntax.push({
          ...this.position(this.token),
          type: ['variable', 'key'],
          error: unknown && 'invalid key identifier',
        })
        key = {
          type: 'value',
          value: this.token.text,
          error: unknown,
        }
        value = { type: 'reference', callable: true, error: true }
        this.next()
        this.passSpaces()
        break
      // a number key
      case 'number':
      case 'unknown_number':
        // eslint-disable-next-line no-redeclare
        var unknown = this.token.type === 'unknown_number'
        if (unknown) error = true
        this.syntax.push({
          ...this.position(this.token),
          type: ['number', 'key'],
          error: unknown && 'invalid number key',
        })
        key = {
          type: 'value',
          value: parseFloat(this.token.text),
          cast: 'number',
          error: unknown,
        }
        this.next()
        this.passSpaces()
        break
      // a string key
      case 'string_begin':
        key = this.parseString(null, ['key'])
        if (key.error) error = true
        this.passSpaces()
        break
      default:
        // computed key
        if (this.token.text === '[') {
          position = this.position(this.token)
          var bsyntax = {
            ...position,
            type: ['operator', 'get'],
          }
          this.syntax.push(bsyntax)
          this.next()
          this.passSpaces()
          // parse the value of the key
          key = this.parseValue()
          // the computed key is closed
          if (this.token && this.token.text === ']') {
            this.syntax.push({
              ...this.position(this.token),
              type: ['operator', 'get'],
            })
            this.next()
            this.passSpaces()
            // the key has no value
            if (!key) bsyntax.error = 'key required'
            // the type of the key is not correct
            else if (key.cast && !BASE_TYPES[key.cast]) {
              error = true
              key.error = true
              bsyntax.error = 'type "' + key.cast + '" is not a valid key type'
            // the key has an error
            } else if (key.error) error = true
          // the coputed key is not closed
          } else {
            error = true
            if (key) key.error = true
            bsyntax.error = 'unclosed key'
            // everything has been read already
            value = null
          }
        // anything else is an error
        } else error = true
        break
      }
      // search for more unexpected value
      if (!value && this.token && this.token.text !== ':' &&
        this.token.text !== ',' && this.token.text !== '}' &&
        (!this.newline || this.token.text === '.')) {
        error = true
        value = this.parseValue(null, value)
        if (this.syntax.length > length) this.syntax[length].error = 'key expected'
        key = null
      }
      // next should be a value
      if (this.token && !this.newline && this.token.text === ':') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['separator', 'colon'],
          error: !key && 'key expected',
        })
        this.next()
        this.passSpaces()
        // read the value
        if (this.token && this.token.text !== '}' && this.token.text !== ',' &&
          this.token.text !== ':') {
          value = this.parseValue()
          if (!value || value.error) error = true
          this.passSpaces()
        } else {
          value = null
          error = true
        }
      } else {
        error = true
        if (key) this.syntax[length].error = 'key expected'
      }
      // save the key / value pair
      if (!error && !key.resolve) {
        if (seen[key.value]) {
          error = true
          this.syntax[length].error = 'duplicate key'
        } else seen[key.value] = true
      }
      if (!error) items.push({ key: key, value: value, position: position })
      // treat those cases next cycle
      if (!this.token || this.token.text === ':') {
        continue
      // comma after the item, normal
      } else if (this.token.text === ',') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['separator', 'comma'],
          error: !value && 'value expected',
        })
        this.next()
        this.passSpaces()
        continue
      // end of the object without comma, no problem
      } else if (this.token.text === '}') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['group', 'object'],
          error: !value && 'value expected',
        })
        this.next()
        break
      // a character that doesn't belong to the object
      } else if (!this.newline) {
        syntax.error = 'no closing character'
        return
      }
    }
    let out = {
      type: 'value',
      cast: 'object',
      error: error,
    }
    if (!error) {
      out.resolve = (function (items) {
        return function (variables) {
          let out = {}
          for (let item of items) {
            let key = item.key
            if (key.resolve) {
              key = key.resolve(variables)
            } else key = key.value
            key = TO_STRING(key)
            let value = item.value
            out[key] = value.resolve ? value.resolve(variables) : value.value
          }
          return out
        }
      })(items)
      out.debug = {
        type: 'object',
        items: items,
      }
    }
    return out
  }
  // parses an array, mainly relies on parseItems
  parseArray (error) {
    // console.log('parseArray')
    // get items
    let items = this.parseItems('array', error)
    if (!items) return
    let out = {
      type: 'value',
      cast: 'array',
      error: items.error,
    }
    if (!out.error) {
      out.resolve = (function (items) {
        return function (variables) {
          return items.map(item => {
            if (item.resolve) return item.resolve(variables)
            return item.value
          })
        }
      })(items.items)
      out.debug = {
        type: 'array',
        items: items.items,
      }
    }
    return out
  }
  // parse a function call, mainly relies on parseItems
  parseCall (left, error) {
    // console.log('parseCall', left)
    let position = this.position(this.token)
    // check that what is called can be a function
    if (left.cast && left.cast !== 'function') {
      error = 'only functions can be called'
    } else if (left.type === 'reference' && !left.callable) {
      error = 'only functions can be called'
    }
    // get items
    let items = this.parseItems('call', error)
    if (!items) return
    let out = {
      type: 'value',
      error: left.error || items.error,
    }
    if (!out.error) {
      out.resolve = (function (left, args, position) {
        return function (variables) {
          let l = left.resolve ? left.resolve(variables) : left.value
          // check that it is a function
          if (typeof l !== 'function') {
            throw new ParserTypeError('type "' + TYPEOF(l) + '" is not callable', position)
          }
          let values = args.map(item => {
            if (item.resolve) return item.resolve(variables)
            return item.value
          })
          try {
            return l.apply(null, values)
          // fill the stack trace
          } catch (error) {
            if (!(error instanceof ParserError)) {
              error = new ParseRuntimeError(error)
            }
            error.codestack.push(position)
            throw error
          }
        }
      })(left, items.items, position)
      out.debug = {
        type: 'call',
        left: left,
        args: items.items,
      }
    }
    return out
  }
  // parses a comma separated list of values
  parseItems (type, error) {
    // console.log('parseItems')
    let syntax = {
      ...this.position(this.token),
      type: ['group', type],
      error: error,
    }
    // the expected closing character
    let closing = CLOSING[this.token.text]
    this.syntax.push(syntax)
    let items = []
    error = !!error
    while (true) {
      this.next()
      this.passSpaces()
      // finished too early
      if (!this.token) {
        syntax.error = 'no closing character'
        return
      // end of the object
      } else if (this.token.text === closing) {
        break
      // a comma without any value before
      } else if (this.token.text === ',') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['separator', 'comma'],
          error: 'item expected',
        })
      } else {
        // parse the item
        let item = this.parseValue()
        if (item) {
          items.push(item)
          if (item.error) error = true
          this.passSpaces()
        } else {
          error = true
          this.passSpaces()
        }
        // end of the object without a comma
        if (this.token && this.token.text === closing) {
          break
        // a comma after an item
        } else if (this.token && this.token.text === ',') {
          this.syntax.push({
            ...this.position(this.token),
            type: ['separator', 'comma'],
          })
        // this character doesn't beong to the object
        } else {
          syntax.error = 'no closing character'
          return
        }
      }
    }
    this.syntax.push({
      ...this.position(this.token),
      type: ['group', type],
    })
    this.next()
    return {
      type: 'items',
      items: items,
      error: error,
    }
  }
  // parses a unary operator
  parseUnary (error) {
    // console.log('parseUnary')
    // check for non unary operators
    let operator = this.token.text, priority = 100
    if (this.token.type === 'unary' || this.token.type === 'mixed') {
    } else if (this.token.type === 'unknown_operator') {
      error = 'unknown operator'
    } else if (this.token.type === 'binary') {
      priority = PRIORITY[operator]
      error = 'unexpected binary operator without left operande'
    } else if (this.token.type === 'assign') {
      priority = 1
      error = 'unexpected assignement operator'
    }
    let position = this.position(this.token)
    let syntax = {
      ...position,
      type: ['operator'],
      error: error,
    }
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // get right operande
    let right = this.parseValue(priority)
    if (!right && !syntax.error) syntax.error = 'right operande required'
    let out = {
      type: 'value',
      error: !!error || !right || right.error,
    }
    if (!out.error) {
      if (!right.resolve) {
        try {
          out.value = UNARY[operator](right.value)
        } catch (error) {
          syntax.error = error.name + ': ' + error.message
        }
      } else {
        out.resolve = (function (operator, right, position) {
          return function (variables) {
            let r = right.resolve(variables)
            try {
              return operator(r)
            } catch (error) {
              if (!(error instanceof ParserError)) {
                error = new ParseRuntimeError(error)
              }
              error.codestack.push(position)
              throw error
            }
          }
        })(UNARY[operator], right, position)
        out.debug = {
          resolve: 'unary',
          operator: operator,
          right: right,
        }
      }
    }
    return out
  }
  // parses a binary operator
  parseBinary (left, error) {
    // console.log('parseBinary', left)
    // check for non binary operators
    let operator = this.token.text, priority //, mergeop
    let position = this.position(this.token), type = this.token.type
    let syntax = {
      ...position,
      type: ['operator'],
      error: error,
    }
    error = !!error
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // // try to merge operators
    // if (this.token && !this.newline && (this.token.type === 'binary' ||
    //   this.token.type === 'mixed' || this.token.type === 'unary') &&
    //   (type === 'binary' || type === 'mixed' || type === 'unary')) {
    //   mergeop = operator + ' ' + this.token.text
    // }
    // if (mergeop && BINARY[mergeop]) {
    //   syntax = {
    //     ...this.position(this.token),
    //     type: ['operator'],
    //   }
    //   this.syntax.push(syntax)
    //   this.next()
    //   this.passSpaces()
    //   operator = mergeop
    //   priority = PRIORITY[mergeop]
    // } else
    if (type === 'binary' || type === 'mixed') {
      priority = PRIORITY[operator]
    } else if (type === 'unknown_operator') {
      priority = 1
      error = true
      syntax.error = 'unknown operator'
    } else if (type === 'unary') {
      priority = 100
      error = true
      syntax.error = 'unexpected unary operator after left operande'
    } else if (type === 'assign') {
      priority = 1
      error = true
      syntax.error = 'unexpected assignement operator'
    }
    // get right operande
    let right = this.parseValue(priority)
    if (!right && !syntax.error) syntax.error = 'right operande required'
    let out = {
      type: 'value',
      error: !!error || !left || left.error || !right || right.error,
    }
    if (!out.error) {
      if (CHAINABLE[operator]) {
        if (left.chain) {
          left.chain.values.push(right)
          left.chain.operators.push(BINARY[operator])
          left.chain.values.push(position)
          return left
        } else {
          let values = [left, right]
          let operators = [BINARY[operator]]
          let positions = [position]
          out.chain = {
            values: values,
            operators: operators,
            positions: positions,
          }
          out.resolve = (function (values, operators, positions) {
            return function (variables) {
              let left = values[0]
              if (left.resolve) left = left.resolve(variables)
              else left = left.value
              for (let i = 0; i < operators.length; i++) {
                let right = values[i + 1]
                if (right.resolve) right = right.resolve(variables)
                else right = right.value
                try {
                  if (!operators[i](left, right)) return false
                } catch (error) {
                  if (!(error instanceof ParserError)) {
                    error = new ParseRuntimeError(error)
                  }
                  error.codestack.push(positions[i])
                  throw error
                }
                left = right
              }
              return true
            }
          })(values, operators, positions)
          out.debug = {
            resolve: 'chain',
            values: values,
            operators: operators,
          }
        }
      } else if (!left.resolve && !right.resolve) {
        try {
          out.value = BINARY[operator](left.value, right.value)
        } catch (error) {
          syntax.error = error.name + ': ' + error.message
        }
      // no need for lazy operator
      } else if (!right.resolve || !LAZY_BINARY[operator]) {
        out.resolve = (function (operator, left, right, position) {
          return function (variables) {
            let l = left.resolve ? left.resolve(variables) : left.value
            let r = right.resolve ? right.resolve(variables) : right.value
            try {
              return operator(l, r)
            } catch (error) {
              if (!(error instanceof ParserError)) {
                error = new ParseRuntimeError(error)
              }
              error.codestack.push(position)
              throw error
            }
          }
        })(BINARY[operator], left, right, position)
        out.debug = {
          resolve: 'binary',
          operator: operator,
          left: left,
          right: right,
        }
      // lazy operator required
      } else {
        out.resolve = (function (operator, left, right, position) {
          return function (variables) {
            let l = left.resolve ? left.resolve(variables) : left.value
            try {
              return operator(l, right, variables)
            } catch (error) {
              if (!(error instanceof ParserError)) {
                error = new ParseRuntimeError(error)
              }
              error.codestack.push(position)
              throw error
            }
          }
        })(LAZY_BINARY[operator], left, right, position)
        out.debug = {
          resolve: 'lazy_binary',
          operator: operator,
          left: left,
          right: right,
        }
      }
    }
    return out
  }
  // parses an assignation operator
  parseAssign (left, error) {
    // console.log('parseAssign', left)
    let operator = this.token.text
    let position = this.position(this.token)
    let syntax = {
      ...position,
      type: ['operator'],
      error: !left.editable ? 'not editable' : error,
    }
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // get right operande
    let right = this.parseValue()
    if (!right && !syntax.error) syntax.error = 'right operande required'
    let out = {
      type: 'command',
      error: !!error || !left || left.error || !left.editable ||
        !right || right.error,
    }
    if (!out.error) {
      // no need for lazy operator
      if (!right.resolve || !LAZY_ASSIGN[operator]) {
        out.resolve = (function (operator, left, right) {
          return function (variables) {
            let l = left.resolve(variables, true)
            let r = right.resolve ? right.resolve(variables) : right.value
            try {
              l.assign(operator(l.value, r))
            } catch (error) {
              if (!(error instanceof ParserError)) {
                error = new ParseRuntimeError(error)
              }
              error.codestack.push(position)
              throw error
            }
          }
        })(ASSIGN[operator], left, right, position)
        out.debug = {
          resolve: 'assign',
          operator: operator,
          left: left,
          right: right,
        }
      // lazy operator required
      } else {
        out.resolve = (function (operator, left, right) {
          return function (variables) {
            let l = left.resolve(variables, true)
            try {
              l.assign(operator(l.value, right, variables))
            } catch (error) {
              if (!(error instanceof ParserError)) {
                error = new ParseRuntimeError(error)
              }
              error.codestack.push(position)
              throw error
            }
          }
        })(LAZY_ASSIGN[operator], left, right, position)
        out.debug = {
          resolve: 'lazy_assign',
          operator: operator,
          left: left,
          right: right,
        }
      }
    }
    return out
  }
  // parses ternary opertor ` a ? b : c`
  parseTernary (condition, error) {
    // console.log('parseTernary')
    let iftrue = false, iffalse
    let syntax = {
      ...this.position(this.token),
      type: ['operator'],
      error: !condition ? 'missing left operande' : error,
    }
    error = !!error || !condition || condition.error
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // finishing much too early
    if (!this.token) {
      if (!syntax.error) syntax.error = 'missing character ":"'
      return
    }
    // no middle operande
    if (this.token.text === ':') {
      if (!syntax.error) syntax.error = 'value expected after'
      error = true
    // get middle operande
    } else if (this.token.text !== ':') {
      iftrue = this.parseValue()
      this.passSpaces()
      // operator ":" not found
      if (!this.token || this.newline || this.token.text !== ':') {
        if (!syntax.error) syntax.error = 'missing character ":"'
        return
      }
    }
    if (!iftrue || iftrue.error) error = true
    syntax = {
      ...this.position(this.token),
      type: ['operator'],
      error: iftrue === false && 'value expected before',
    }
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // finishing too early
    if (!this.token) {
      if (!syntax.error) syntax.error = 'value expected after'
      return
    }
    // get right operande
    iffalse = this.parseValue()
    // right operande wasn't found
    if (!iffalse && !syntax.error) syntax.error = 'value expected after'
    if (!iffalse || iffalse.error) error = true
    let out = {
      type: 'value',
      error: error,
    }
    if (!error) {
      if (!condition.resolve) {
        return TRUE(condition.value) ? iftrue : iffalse
      } else {
        out.resolve = (function (condition, iftrue, iffalse) {
          return function (variables) {
            let c = condition.resolve(variables)
            if (TRUE(c)) {
              return iftrue.resolve ? iftrue.resolve(variables) : iftrue.value
            } else {
              return iffalse.resolve ? iffalse.resolve(variables) : iffalse.value
            }
          }
        })(condition, iftrue, iffalse)
        out.debug = {
          type: 'ternary',
          condition: condition,
          iftrue: iftrue,
          iffalse: iffalse,
        }
      }
    }
    return out
  }
  // parses a number
  parseNumber (error) {
    // console.log('parseNumber')
    this.syntax.push({
      ...this.position(this.token),
      type: ['number'],
      error: this.token.type === 'number' ? error : 'wrong number format',
    })
    let out
    if (this.token.type === 'number') {
      out = {
        type: 'value',
        cast: 'number',
        value: parseFloat(this.token.text),
        error: !!error,
      }
    } else {
      out = {
        type: 'value',
        cast: 'number',
        error: true,
      }
    }
    this.next()
    return out
  }
  // parse a string
  parseString (error, type) {
    // console.log('parseString')
    let _type = ['string']
    if (type) _type.push(...type)
    let string = [], length = 1, begin = this.token
    while (true) {
      this.next()
      // update the length of the piece of code for this string
      if (this.token) length += this.token.text.length
      // a newline without string end
      if (!this.token || this.token.type === 'expected_string_end') {
        this.syntax.push({
          ...this.position(begin, length),
          type: _type,
          error: 'string end `' + begin.text + '` expected',
        })
        if (this.token) this.next()
        return {
          type: 'value',
          cast: 'string',
          error: true,
        }
      // a new line of string to add
      } else if (this.token.type === 'string') string.push(this.token.text)
      // end of the string enventually
      else if (this.token.type === 'string_end') {
        string = string.join('')
        // // little gymnastic to parse baclslashes
        // if (begin.text === "'") {
        //   string = string.replace(/\\'/g, "'").replace(/"/g, '\\"')
        // }
        // try {
        //   string = JSON.parse('"' + string + '"')
        // } catch (e) {
        //   error = e.message
        // }
        let escapes = { '\n': '', 'n': '\n', 't': '\t' }
        string = string.replace(/\\(.)/,
          (_, c) => c in escapes ? escapes[c] : c)
        this.syntax.push({
          ...this.position(begin, length),
          type: _type,
          error: error,
        })
        this.next()
        return {
          type: 'value',
          cast: 'string',
          value: string,
          error: !!error,
        }
      }
    }
  }
  // parses a litteral
  parseLiteral (error) {
    // console.log('parseLiteral')
    this.syntax.push({
      ...this.position(this.token),
      type: ['literal'],
      error: error,
    })
    let name = this.token.text
    let out = {
      type: 'value',
      value: LITERAL_VALUE[name],
      cast: LITERAL_CAST[name],
      error: !!error,
    }
    this.next()
    return out
  }
  // used by parseGetter and parseVariable
  // parses a path with "." and "[]" operators
  // * path: the path to continue
  // * error: true if should be in error
  // * posistions: the positions to continue
  // * contexts: true if next literal will be a context
  parsePath (path, parent, error, positions, contexts) {
    // console.log('parsePath')
    path = path || []
    error = error || false
    // for stacktrace
    positions = positions || []
    while (true) {
      let suggestions = [], allowed = null, nextValue = false
      if (parent && parent.type === 'value') {
        if (parent.cast === 'object' || parent.cast === 'module') {
          if (parent.value) {
            nextValue = true
            allowed = {}
            if (parent.cast === 'module') {
              for (let p of parent.value.modules) {
                allowed[p.name] = true
                suggestions.push({
                  ...p,
                  right: p.name,
                })
              }
              if (!Module.loaded) allowed = null
            } else {
              for (let name in parent.value) {
                allowed[name] = true
                suggestions.push({
                  name: name,
                  right: name,
                  cast: TYPEOF(parent.value[name]),
                })
              }
            }
          }
        } else if (parent.cast || parent.clue) {
          let proto = this.prototype[parent.cast || parent.clue] ||
            Module.loaded && {}
          if (proto instanceof Module) {
            allowed = {}
            for (let p of proto.prototypes) {
              allowed[p.name] = true
              suggestions.push({
                ...p,
                right: p.name,
              })
            }
            if (!Module.loaded) allowed = null
          } else if (proto) {
            allowed = {}
            for (let name in proto) {
              allowed[name] = true
              suggestions.push({
                name: name,
                right: name,
                cast: 'function',
              })
            }
          }
          if (!parent.cast) allowed = null
        }
      }
      // found a "."
      if (this.token && this.token.text === '.') {
        let position = this.position(this.token)
        let syntax = {
          ...position,
          type: ['operator', 'dot'],
        }
        this.syntax.push(syntax)
        this.next()
        this.passSpaces()
        // not followed by an identifier
        if (!this.token || this.token.type !== 'identifier' &&
          this.token.type !== 'unknown_identifier') {
          syntax.suggestions = suggestions
          syntax.append = true
          syntax.error = 'variable identifier required'
          return
        }
        let name = this.token.text
        error = error || this.token.type === 'unknown_identifier' ||
          allowed && !allowed[name]
        path.push(name)
        positions.push(position)
        this.syntax.push({
          ...this.position(this.token),
          type: contexts ? ['variable', 'context'] : ['variable'],
          suggestions: filterSuggestions(name, suggestions, 'name'),
          error: this.token.type === 'unknown_identifier'
            ? 'invalid variable identifier'
            : !allowed || allowed[name] ? false
              : nextValue ? 'no field "' + name + '"'
                : 'no method "' + name + '"',
        })
        contexts = false
        if (nextValue && allowed[name]) {
          if (parent.value instanceof Module) {
            parent = parent.value.items[name]
          } else parent = parent.value[name]
          parent = {
            type: 'value',
            value: parent,
            cast: TYPEOF(parent),
          }
        } else parent = null
      // found a getter []
      } else if (this.token && !this.newline && this.token.text === '[') {
        parent = null
        let position = this.position(this.token)
        let syntax = {
          ...position,
          type: ['operator', 'get'],
        }
        this.syntax.push(syntax)
        this.next()
        this.passSpaces()
        // read the value inside the getter
        let value = this.parseValue()
        // getter closed
        if (this.token && this.token.text === ']') {
          // check the key
          if (!value) {
            error = true
            syntax.error = 'key required'
          } else if (value.cast && !BASE_TYPES[value.cast]) {
            error = true
            value.error = true
            syntax.error = 'type "' + value.cast + '" is not a valid key type'
          } else if (value.error) {
            error = true
          // add the key
          } else {
            path.push(value)
            positions.push(position)
          }
          this.syntax.push({
            ...this.position(this.token),
            type: ['operator', 'get'],
          })
        // getter not closed
        } else {
          syntax.error = 'no closing character'
          return
        }
      // finished
      } else {
        return {
          path: path,
          positions: positions,
          error: error,
        }
      }
      this.next()
      this.passSpaces()
    }
  }
  // parses a getter, a serie of "." and "[]" operators
  parseGetter (left, path) {
    // console.log('parseGetter')
    // get the path
    path = path || this.parsePath(null, left)
    if (!path) return { type: 'reference', error: true }
    let out = {
      type: 'reference',
      error: left.error || path.error,
      // non editable object are copied, result is editable
      editable: !left.cast || left.cast === 'object' || left.cast === 'array',
      // global functions must be referenced with full path
      // and local callable variables have no path
      // however prototypes can be called
      callable: path.path.length === 1 && left.cast !== 'object',
      // local freevar variables have no path
      freevar: false,
    }
    if (!path.error) {
      out.resolve = this.createResolvePath((function () {
        return function (variables) {
          return left.resolve ? left.resolve(variables) : left.value
        }
      })(left), 0, path.path, path.positions, 'editable')
      out.debug = {
        type: 'getter',
        left: left,
        path: path.path,
      }
    }
    return out
  }
  // used by parseVariable, get the call path of a variable declaration
  parseVariablePath (error) {
    // console.log('parseVariablePath')
    let begin = this.token, length = 0, prefix = '', suggestions = []
    // look for a prefix
    if (this.token.type === 'prefix') {
      length = this.token.text.length
      prefix = this.token.text
      this.next()
      // no variable name after the prefix
      if (this.prefix[prefix] && (!this.token ||
        this.token.type !== 'identifier' &&
        this.token.type !== 'unknown_identifier')) {
        this.syntax.push({
          ...this.position(begin),
          type: ['variable', this.prefix[prefix]],
          error: 'variable identifier required',
          suggestions: suggestions,
        })
        return
      }
    } else if (this.token.text[0] < 'A' || this.token.text[0] > 'Z') {
      prefix = '@@'
    }
    if (prefix) {
      for (let name in this.variables[prefix] || {}) {
        suggestions.push({
          name: name,
          right: name,
          cast: TYPEOF(this.variables[prefix][name]),
        })
      }
    } else if (Module.loaded) {
      for (let name in this.variables.global) {
        suggestions.push({
          name: name,
          right: name,
          // value: this.variables.global[name],
          cast: TYPEOF(this.variables.global[name]),
        })
      }
    }
    if (this.token && this.token.type === 'unknown_identifier') {
      error = 'invalid variable identifier'
    } else if (!this.prefix[prefix]) {
      error = (this.prefix[''] ? prefix + ' ' : '') + 'variables not allowed'
    }
    let name = this.token.text, value = null
    prefix = this.prefix[prefix]
    if (prefix === this.prefix[''] && Module.loaded) {
      if (!error && !(name in this.variables.global)) {
        error = 'no module "' + name + '"'
      }
      value = {
        type: 'value',
        value: this.variables.global[name],
        cast: TYPEOF(this.variables.global[name]),
      }
    } else if (name in (this.variables[prefix] || {})) {
      value = {
        type: 'value',
        clue: TYPEOF(this.variables[prefix](name)),
      }
    }
    // the path so far
    let path = [prefix, name]
    let positions = [this.position(begin), this.position(this.token)]
    this.syntax.push({
      ...this.position(begin, length + name.length),
      type: ['variable', prefix],
      error: error,
      suggestions: filterSuggestions(name, suggestions, 'name'),
    })
    this.next()
    this.passSpaces()
    // parse the rest of the path
    return this.parsePath(path, value, !!error, positions, prefix === 'contexts')
  }
  // parses a variable declaration with its path
  parseVariable (path, error) {
    // console.log('parseVariable')
    path = path || this.parseVariablePath(error)
    if (!path) return { type: 'reference', error: true }
    let status = this.status[path.path[0]]
    // if the path can be assigned
    let editable = status === 'freevar' || status === 'editable'
    // if the pass can recieve non JSON values (function, Infinity)
    let freevar = status === 'freevar' && path.path.length === 2
    // if the path can be a callable function
    let callable = freevar || status === 'callable' || path.path.length > 2
    let out = {
      type: 'reference',
      error: path.error,
      // true if can be assigned
      editable: editable,
      // true if can be a function
      callable: callable,
      // true if can receive Javascript-specific values (functions, Infinity)
      freevar: freevar,
    }
    if (!path.error) {
      out.resolve = this.createResolvePath(function (variables, path) {
        return variables[path[0]]
      }, 1, path.path, path.positions, status)
      out.debug = {
        type: 'variable',
        path: path.path,
      }
    }
    return out
  }
  createResolvePath (init, index, path, positions, status) {
    let prototype = this.prototype
    return function (variables, assign) {
      let value = init(variables, path, positions)
      for (let i = index; i < path.length; i++) {
        // get the key
        let key = path[i]
        if (typeof key === 'object' && key !== null) {
          if (key.resolve) key = key.resolve(variables)
          else key = key.value
        }
        // this will end with an assignaton
        if (assign && i + 1 === path.length) {
          // an array, check that the key is a boolean or a valid integer
          if (TYPEOF(value) === 'array') {
            if (!BASE_TYPES[TYPEOF(key)]) throw new ParserTypeError('type "' + TYPEOF(key) + '" is not a valid array key', positions[i])
            let index = TO_KEY(key)
            if (index === null) {
              if (key === 'length') {
                throw new ParserTypeError(
                  'cannot assign length of "array"', positions[i])
              } else if (typeof key === 'string') {
                throw new ParserTypeError(
                  'cannot assign a method of "array"', positions[i])
              } else {
                throw new ParserTypeError('value "' + key +
                '" is not a valid array key', positions[i])
              }
            }
            key = index
            if (key < 0) key += value.length
            let outrange = null
            if (key < 0 || key > value.length) {
              outrange = new ParserTypeError(
                'key "' + key + '" is out of range', positions[i])
            }
            assign = {
              assign: val => {
                if (outrange) throw outrange
                value[key] = val
              },
              delete: () => {
                if (outrange) throw outrange
                value.splice(key, 1)
              },
            }
          // an object, check that the key is valid
          } else if (TYPEOF(value) === 'object') {
            if (!BASE_TYPES[TYPEOF(key)]) {
              throw new ParserTypeError('type "' + TYPEOF(key) + '" is not a valid field name', positions[i])
            }
            key = TO_STRING(key)
            assign = {
              assign: val => { value[key] = val },
              delete: () => { delete value[key] },
            }
          } else throw new ParserTypeError('cannot assign a method of "' + TYPEOF(value) + '"', positions[i])
          let val = value[key]
          if (val === undefined) val = null
          return {
            value: val,
            assign: val => {
              if (!status === 'freevar') {
                if (typeof val === 'function') {
                  throw new ParserTypeError('can only assign function to root local variable', positions[i])
                } else if (typeof val === 'number' && !isFinite(val)) {
                  throw new ParserTypeError('can only assign `Infinity` to root local variable', positions[i])
                }
              }
              if (status === 'freevar' || status === 'editable') {
                assign.assign(val)
              } else {
                throw new ParserTypeError('not editable')
              }
            },
            delete: () => {
              if (status === 'freevar' || status === 'editable') {
                assign.delete()
              } else {
                throw new ParserTypeError('not editable')
              }
            },
          }
        // an array
        } else if (TYPEOF(value) === 'array' || TYPEOF(value) === 'string') {
          if (!BASE_TYPES[TYPEOF(key)]) throw new ParserTypeError('type "' + TYPEOF(key) + '" is not a valid index', positions[i])
          let index = TO_KEY(key)
          if (index === null) {
            if (typeof key !== 'string') {
              throw new ParserTypeError(
                'value "' + key + '" is not a valid index', positions[i])
            } else if (key === 'length') value = value.length
            else {
              let proto = prototype[TYPEOF(value)] || {}
              if (proto instanceof Module) {
                proto = proto.items
              }
              if (!proto.hasOwnProperty(key)) {
                throw new ParserTypeError('type "' + TYPEOF(value) +
                  '" has no method "' + key + '"', positions[i])
              }
              // eventually copy array to avoid modifiation
              if (status !== 'freevar' && status !== 'editable') {
                value = JSON.parse(JSON.stringify(value))
              }
              value = proto[key].bind(null, value)
            }
          } else {
            if (index < 0) index += value.length
            value = value[index]
            if (value === undefined) value = null
          }
        // an object or a module, check that the key is valid
        } else if (typeof value === 'object' && value !== null) {
          if (!BASE_TYPES[TYPEOF(key)]) {
            throw new ParserTypeError('type "' + TYPEOF(key) + '" is not a valid field name', positions[i])
          }
          key = TO_STRING(key)
          if (value instanceof Module) {
            value = value.items
          }
          if (value.hasOwnProperty(key)) {
            value = value[key]
            if (value === undefined) value = null
          } else value = null
        // anything else, check for string methond
        } else {
          if (typeof key !== 'string') throw new ParserTypeError('type "' + TYPEOF(key) + '" is not a valid method name', positions[i])
          let proto = prototype[TYPEOF(value)] || {}
          if (proto instanceof Module) {
            proto = proto.items
          }
          if (proto.hasOwnProperty(key)) {
            value = proto[key].bind(null, value)
          } else throw new ParserTypeError('type "' + TYPEOF(value) + '" has no method "' + key + '"', positions[i])
        }
      }
      if (typeof value === 'object' && value !== null) {
        // check that we are not referencing global object
        if (value instanceof Module) {
          throw new ParserReferenceError('incomplete path', positions[path.length - 1])
        // eventually copy object before returning it
        } else if (status !== 'freevar' && status !== 'editable') {
          return JSON.parse(JSON.stringify(value))
        }
      }
      return value
    }
  }
  // parses a control command (pass, continue, break, return)
  parseControl (inLoop, inFunction, error) {
    // console.log('parseControl')
    let control = this.token.text
    let syntax = {
      ...this.position(this.token),
      type: ['block', 'control'],
      error: error,
    }
    this.syntax.push(syntax)
    this.next()
    let command = {
      type: 'command',
      control: control,
      error: !!error,
    }
    // check if there's value after the control
    this.passSpaces()
    let length = this.syntax.length
    let value = !this.newline && this.parseValue()
    // return, accepts a value
    if (control === 'return') {
      if (!inFunction) {
        command.error = true
        syntax.error = 'unexpected return out of a function'
      }
      if (!value) {
        // check that nothing was parsed
        if (this.syntax.length === length) command.value = null
        // else it's an error
        else command.error = true
      } else if (value.error) {
        command.error = true
      } else if (value.resolve) {
        command.resolve = value.resolve
        command.debug = { value: value }
      } else command.value = value.value
    } else {
      // check that continue and break are in loop
      if (control !== 'pass' && !inLoop) {
        command.error = true
        syntax.error = 'unexpected ' + control + ' out of a loop'
      } else if (this.syntax.length !== length) {
        command.error = true
        syntax.error = 'doesn\'t take any value'
      }
    }
    return command
  }
  // parses a block (for / if / else)
  parseBlock (inLoop, inFunction, error) {
    // console.log('parseBlock')
    let block = this.token.text
    let syntax = {
      ...this.position(this.token),
      type: ['block', block],
      error: error,
    }
    error = !!error
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // parse the condition
    if (block === 'else') {
      error = true
      syntax.error = '`else` block must be after `if` or `for` block'
    } else if (!this.newline && this.token && this.token.text === '(') {
      let psyntax = {
        ...this.position(this.token),
        type: ['group', 'block'],
      }
      this.syntax.push(psyntax)
      this.next()
      this.passSpaces()
      // conditon for "if"
      if (block === 'if') {
        var condition = this.parseValue()
        if (!condition) {
          error = true
          psyntax.error = 'condition expected'
        } else if (condition.error) {
          error = true
        }
      // iteration parameters for "for"
      } else if (block === 'for') {
        var args, operator, value
        let length = this.syntax.length
        // parse arguments
        args = this.parseArguments(null, true)
        if (!args) {
          error = true
          psyntax.error = 'arguments expected'
        } else if (args.error) error = true
        else if (args.names.length !== 1 && args.names.length !== 2) {
          error = true
          this.syntax[length].error = 'one or two arguments expected'
        } else args = args.names
        // parse loop operator
        if (this.token && (this.token.text === 'in' || this.token.text === 'of')) {
          operator = this.token.text
          this.syntax.push({
            ...this.position(this.token),
            type: ['operator', 'block'],
          })
          this.next()
          this.passSpaces()
        } else if (!psyntax.error) {
          error = true
          psyntax.error = 'operator `in` or `of` expected'
        }
        // parse iteration value
        value = this.parseValue()
        if (value) {
          if (value.error) error = true
        } else if (!psyntax.error) {
          error = true
          if (psyntax.error) psyntax.error = 'value expected'
        }
      }
      // closing parenthesis
      if (this.token && this.token.text === ')') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['group', 'block'],
        })
        this.next()
        this.passSpaces()
      } else if (!psyntax.error) {
        error = true
        psyntax.error = 'unclosed parenthesis'
      }
    } else {
      error = true
      syntax.error = 'block arguments expected'
    }
    // look for the following command
    let commands, elsecommands
    // a else directly without any command
    if (this.token && this.token.text === 'else') {
      commands = this.createCommands([])
    // block command
    } else if (this.token && this.token.text === '{') {
      commands = this.parseCommandBlock(inLoop + block === 'for', inFunction)
    // one line command
    } else {
      let command = this.parseCommand(inLoop + block === 'for', inFunction)
      if (command) commands = this.createCommands([command])
    }
    if (!commands) {
      if (!syntax.error) syntax.error = 'command required'
      return
    } else if (commands.error) error = true
    // an "else" block
    if (this.token && this.token.text === 'else' && block !== 'else') {
      syntax = {
        ...this.position(this.token),
        type: ['group', 'block'],
      }
      this.syntax.push(syntax)
      this.next()
      this.passSpaces()
      if (this.token && this.token.text === '{') {
        elsecommands = this.parseCommandBlock(inLoop, inFunction)
      // one line command
      } else {
        let command = this.parseCommand(inLoop, inFunction)
        if (command) elsecommands = this.createCommands([command])
      }
      if (!elsecommands) {
        if (!syntax.error) syntax.error = 'command required'
        return
      } else if (elsecommands.error) error = true
    }
    let out = {
      type: 'command',
      error: error,
    }
    if (!error) {
      if (block === 'if') {
        out.resolve = (function (condition, commands, elsecommands) {
          return function (variables) {
            let c = condition.resolve ? condition.resolve(variables)
              : condition.value
            if (TRUE(c)) {
              return commands.resolve(variables)
            } else if (elsecommands) {
              return elsecommands.resolve(variables)
            }
          }
        })(condition, commands, elsecommands)
        out.debug = {
          condition: condition,
          commands: commands,
          elsecommands: elsecommands,
        }
      } else if (block === 'for') {
        out.resolve = (function (args, operator, value, commands, elsecommands) {
          return function (variables) {
            let v = value.resolve ? value.resolve(variables) : value.value
            // continue is implicitly taken care of
            if (Array.isArray(v)) {
              // x in array: iterate over array keys
              if (operator === 'in') {
                for (let i = 0; i < v.length; i++) {
                  variables.script[args[0]] = i
                  if (args[1]) variables.script[args[1]] = v[i]
                  let r = commands.resolve(variables)
                  if (r.command === 'return') return r
                  else if (r.command === 'break') return
                }
              // x of array: iterate over array items
              } else if (operator === 'of') {
                for (let i = 0; i < v.length; i++) {
                  variables.script[args[0]] = v[i]
                  if (args[1]) variables.script[args[1]] = i
                  let r = commands.resolve(variables)
                  if (r.command === 'return') return r
                  else if (r.command === 'break') return
                }
              }
            } else if (TYPEOF(v) === 'object') {
              // x in object: iterate over array fields
              if (operator === 'in') {
                for (let i in v) {
                  variables.script[args[0]] = i
                  if (args[1]) variables.script[args[1]] = v[i]
                  let r = commands.resolve(variables)
                  if (r.command === 'return') return r
                  else if (r.command === 'break') return
                }
              // x of object: iterate over array items
              } else if (operator === 'of') {
                for (let i in v) {
                  variables.script[args[0]] = v[i]
                  if (args[1]) variables.script[args[1]] = i
                  let r = commands.resolve(variables)
                  if (r.command === 'return') return r
                  else if (r.command === 'break') return
                }
              }
            }
            // else is called if the loop wasn't broken
            if (elsecommands) {
              return elsecommands.resolve(variables)
            }
          }
        })(args, operator, value, commands, elsecommands)
        out.debug = {
          args: args,
          operator: operator,
          value: value,
          commands: commands,
          elsecommands: elsecommands,
        }
      }
    }
    return out
  }
  // parses a function declaration
  parseFunction (error) {
    // console.log('parseFunction')
    let syntax = {
      ...this.position(this.token),
      type: ['block', 'function'],
      error: error,
    }
    error = !!error
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    // get function args
    let args
    // if (this.token && (this.token.type === 'prefix' ||
    //   this.token.type === 'identifier' ||
    //   this.token.type === 'unknown_identifier')) {
    //   args = this.parseOneArgument()
    //   if (!args) {
    //     error = true
    //     syntax.error = 'arguments required'
    //   } else if (args.error) {
    //     error = true
    //   } else args = [args.name]
    // } else {
    args = this.parseArguments()
    if (!args) {
      error = true
      syntax.error = 'arguments required'
    } else if (args.error) {
      error = true
    } else args = args.names
    // }
    // fill the value for one line function with value, else fill commands
    let value, commands
    if (this.token && this.token.text === '{') {
      commands = this.parseCommandBlock(false, true)
      if (!commands) {
        if (!syntax.error) syntax.error = 'command required'
        return
      }
      if (commands.error) error = true
    } else {
      value = this.parseCommand(false, true, true)
      if (!value) {
        if (!syntax.error) syntax.error = 'command required'
        return
      }
      // value is not a value but a command
      if (value.type !== 'value' && value.type !== 'reference') {
        commands = this.createCommands([value])
        if (!commands) return
        if (commands.error) error = true
      } else if (value.error) error = true
    }
    let out = {
      type: 'value',
      cast: 'function',
      error: error,
    }
    if (!error) {
      // just execute the command, eventually follow the return
      if (commands) {
        out.resolve = (function (args, commands) {
          return function (variables) {
            return function (...values) {
              // make a new variables environment
              let copy = { ...variables,
                script: Object.create(variables.script || {}) }
              for (let i = 0; i < args.length; i++) {
                copy.script[args[i]] = values[i]
              }
              let r = commands.resolve(copy)
              if (r.command === 'return') return r.value
              return null
            }
          }
        })(args, commands)
        out.debug = {
          type: 'function',
          args: args,
          commands: commands,
        }
      } else {
        // evaluate the value and return it
        out.resolve = (function (args, value) {
          return function (variables) {
            return function (...values) {
              if (value.resolve) {
                // make a new variables environment
                let copy = { ...variables,
                  script: Object.create(variables.script || {}) }
                for (let i = 0; i < args.length; i++) {
                  copy.script[args[i]] = values[i]
                }
                return value.resolve(copy)
              } else return value.value
            }
          }
        })(args, value)
        out.debug = {
          type: 'lambda',
          args: args,
          value: value,
        }
      }
    }
    return out
  }
  // parses a list of arguments, either one alone, either a list between parenthesis
  parseArguments (error, noparenthesis) {
    // console.log('parseArguments', this.index, noparenthesis)
    if (!this.token) return
    let parenthesis = this.token.text === '(', syntax = null
    if (parenthesis) {
      syntax = {
        ...this.position(this.token),
        type: ['group', 'arguments'],
        error: error,
      }
      this.syntax.push(syntax)
      this.next()
      this.passSpaces()
    } else if (!noparenthesis) {
      return
    }
    let args = [], comma_error = null
    error = !!error
    while (true) {
      this.identify()
      // finishes too early
      if (!this.token) {
        // end parenthesis expected
        if (parenthesis) {
          syntax.error = 'no closing character'
          return
        // no parenthesis expected, do not deal with it
        } else break
      // an argument
      } else if (this.token.type === 'prefix' ||
        this.token.type === 'identifier' ||
        this.token.type === 'unknown_identifier') {
        // let length = this.syntax.length
        let out = this.parseOneArgument(comma_error)
        comma_error = null
        if (out) {
          args.push(out.name)
          if (out.error) error = true
          this.passSpaces()
        } else {
          error = true
        }
        // let next loop turn deal with it
        if (!this.token) {
          continue
        // the expected comma
        } else if (this.token.text === ',') {
          this.syntax.push({
            ...this.position(this.token),
            type: ['separator', 'comma'],
          })
          this.next()
          this.passSpaces()
        // another variable already
        } else if (this.token.type === 'prefix' ||
          this.token.type === 'identifier' ||
          this.token.type === 'unknown_identifier') {
          // an error to be displayed on the next variable
          comma_error = 'comma expected'
        // whatever should not be here
        } else if (parenthesis && this.token && this.token.text !== ')') {
          error = true
          let length = this.syntax.length
          this.parseValue(null, null, 'comma expected')
          // ano value was found
          if (this.syntax.length === length) {
            syntax.error = 'no closing character'
            return
          }
          // finally a comma
          if (this.token && this.token.text === ',') {
            this.syntax.push({
              ...this.position(this.token),
              type: ['separator', 'comma'],
            })
            this.next()
            this.passSpaces()
          }
        }
        // else let next loop turn deal with it
      // no argument before this comma
      } else if (this.token.text === ',') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['separator', 'comma'],
          error: 'script variable expected',
        })
        this.next()
        this.passSpaces()
      // no parenthesis to end, so end as soon as something weird is met
      } else if (!parenthesis) {
        break
      // end of the list
      } else if (this.token.text === ')') {
        this.syntax.push({
          ...this.position(this.token),
          type: ['group', 'arguments'],
        })
        this.next()
        this.passSpaces()
        break
      // whatever should not be here
      } else {
        let length = this.syntax.length
        this.parseValue(null, null, 'script variable expected')
        if (this.syntax.length === length) {
          syntax.error = 'no closing character'
          return
        }
      }
    }
    return { type: 'args', names: args, error: error }
  }
  // parses exactly on argument
  parseOneArgument (error) {
    // console.log('parseOneArgument', this.index)
    let begin = this.token, length = 0, prefix = ''
    // prefix should only be local variable
    if (this.token.type === 'prefix') {
      length = this.token.text.length
      prefix = this.token.text
      this.next()
    }
    // a variable
    if (this.token && (this.token.type === 'identifier' ||
        this.token.type === 'unknown_identifier')) {
      if (!prefix && (this.token.text[0] < 'A' || this.token.text[0] > 'Z')) {
        prefix = '@@'
      }
      if (this.prefix[prefix] !== 'script') {
        error = 'script variable required'
      } else if (this.token.type === 'unknown_identifier') {
        error = 'invalid variable name'
      }
      let syntax = {
        ...this.position(begin, length + this.token.text.length),
        type: ['variable', 'argument'],
        error: error,
      }
      this.syntax.push(syntax)
      let out = { type: 'arg', name: this.token.text, error: !!error }
      this.next()
      this.passSpaces()
      if (this.token && (this.token.text === '.' ||
          !this.newline && this.token.text === '[')) {
        out.error = true
        syntax.error = 'no object field allowed'
        this.parseGetter(out)
      }
      return out
    // no variable
    } else {
      this.syntax.push({
        ...this.position(begin),
        type: ['variable', 'argument'],
        error: 'identifier required',
      })
      this.passSpaces()
      return
    }
  }
  // parses a delete commad
  parseDelete (error) {
    let position = this.position(this.token)
    let syntax = {
      ...position,
      type: ['operator', 'delete'],
      error: error,
    }
    this.syntax.push(syntax)
    this.next()
    this.passSpaces()
    error = !!error
    let reference = !this.newline && this.parseValue()
    // nothing after delete
    if (!reference) {
      syntax.error = 'reference required'
      error = true
    // not a reference after delete
    } else if (reference.type !== 'reference') {
      syntax.error = 'reference expected'
      error = true
    // the reference after delete is not editable
    } else if (!reference.editable) {
      syntax.error = 'not editable'
      error = true
    // error
    } else if (reference.error) {
      error = true
    }
    let out = {
      type: 'command',
      error: error,
    }
    if (!error) {
      out.resolve = (function (reference, position) {
        return function (variables) {
          let r = reference.resolve(variables, true)
          // if this call fail, fill the stack
          try {
            r.delete()
          } catch (error) {
            if (!(error instanceof ParserError)) {
              error = new ParseRuntimeError(error)
            }
            error.codestack.push(position)
            throw error
          }
        }
      })(reference, position)
      out.debug = {
        type: 'delete',
        reference: reference,
      }
    }
    return out
  }
  // push syntax with error for anything that comes after
  parseUnexpected (error) {
    // console.log('parseUnexpected')
    if (this.token.type === 'identifier') {
      this.token.type = IDENTIFIERS[this.token.text] || 'identifier'
    }
    switch (this.token.type) {
    case 'number':
      this.syntax.push({
        ...this.position(this.token),
        type: ['number'],
        error: error || 'unexpected number',
      })
      return this.next()
    case 'unknown_number':
      this.syntax.push({
        ...this.position(this.token),
        type: ['number'],
        error: 'wrong number format',
      })
      return this.next()
    case 'string_begin':
      // eslint-disable-next-line no-redeclare
      var length = this.syntax.length
      this.parseString()
      if (this.syntax[length].error) {
        this.syntax[length].error = error || 'unexpected string'
      }
      return
    case 'literal':
      this.syntax.push({
        ...this.position(this.token),
        type: ['literal'],
        error: error || 'unexpected literal',
      })
      return this.next()
    case 'prefix':
      // eslint-disable-next-line no-redeclare
      var begin = this.token
      this.next()
      if (this.token && this.token.type === 'unknown_identifier') {
        this.syntax.push({
          ...this.position(begin, begin.text + this.token.text),
          type: ['variable'],
          error: 'invalid variable name',
        })
        return this.next()
      } else if (this.token && this.token.type === 'identifier') {
        this.syntax.push({
          ...this.position(begin, begin.text + this.token.text),
          type: ['variable'],
          error: error || 'unexpected variable',
        })
        return this.next()
      } else {
        this.syntax.push({
          ...this.position(begin),
          type: ['variable'],
          error: error || 'unexpected variable',
        })
        return
      }
    case 'identifier':
      this.syntax.push({
        ...this.position(this.token),
        type: ['variable'],
        error: error || 'unexpected variable',
      })
      return this.next()
    case 'unknown_identifier':
      this.syntax.push({
        ...this.position(this.token),
        type: ['variable'],
        error: 'invalid variable name',
      })
      return this.next()
    case 'special':
    case 'assign':
    case 'binary':
    case 'mixed':
    case 'unary':
    case 'delete':
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator'],
        error: error || 'unexpected operator',
      })
      return this.next()
    case 'unknown_operator':
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator'],
        error: 'unknown operator',
      })
      return this.next()
    case 'block':
      this.syntax.push({
        ...this.position(this.token),
        type: ['block'],
        error: error || 'unexpected block',
      })
      return this.next()
    case 'function':
      this.syntax.push({
        ...this.position(this.token),
        type: ['block'],
        error: error || 'unexpected function',
      })
      return this.next()
    case 'control':
      this.syntax.push({
        ...this.position(this.token),
        type: ['block'],
        error: error || 'unexpected control',
      })
      return this.next()
    case 'comment':
      this.syntax.push({
        ...this.position(this.token),
        type: ['comment'],
        error: error || 'unexpected comment',
      })
      return this.next()
    case 'group':
      this.syntax.push({
        ...this.position(this.token),
        type: ['group'],
        error: error || 'unexpected group character',
      })
      return this.next()
    case 'space':
      this.syntax.push({
        ...this.position(this.token),
        type: ['error'],
        error: error || 'unexpected space',
      })
      return this.next()
    case 'newline':
      this.syntax.push({
        ...this.position(this.token),
        type: ['error'],
        error: error || 'unexpected space',
      })
      return this.next()
    case 'unknown_char':
      // eslint-disable-next-line no-redeclare
      var begin = this.token, length = 0
      while (this.token && this.token.type === 'unknown_char') {
        length += this.token.text.length
        this.next()
      }
      this.syntax.push({
        ...this.position(begin, length),
        type: ['error'],
        error: error || 'unexpected character',
      })
      return
    default:
      this.syntax.push({
        ...this.position(this.token),
        type: ['error'],
        error: error || 'unexpected thing',
      })
      return this.next()
    }
  }
  // push syntax without error for anything that comes after
  parseUnchecked (error) {
    switch (this.token.type) {
    case 'number':
      this.syntax.push({
        ...this.position(this.token),
        type: ['number'],
        error: error,
      })
      return this.next()
    case 'unknown_number':
      this.syntax.push({
        ...this.position(this.token),
        type: ['number'],
        error: 'wrong number format',
      })
      return this.next()
    case 'string_begin':
      var length = this.syntax.length
      this.parseString()
      if (this.syntax[length].error) {
        this.syntax[length].error = error
      }
      return
    case 'literal':
      this.syntax.push({
        ...this.position(this.token),
        type: ['literal'],
        error: error,
      })
      return this.next()
    case 'prefix':
      // eslint-disable-next-line no-redeclare
      var begin = this.token
      this.next()
      if (this.token && this.token.type === 'unknown_identifier') {
        this.syntax.push({
          ...this.position(begin, begin.text + this.token.text),
          type: ['variable'],
          error: 'invalid variable name',
        })
        return this.next()
      } else if (this.token && this.token.type === 'identifier') {
        this.syntax.push({
          ...this.position(begin, begin.text + this.token.text),
          type: ['variable'],
          error: error,
        })
        return this.next()
      } else {
        this.syntax.push({
          ...this.position(begin),
          type: ['variable'],
          error: error,
        })
        return
      }
    case 'identifier':
      this.syntax.push({
        ...this.position(this.token),
        type: ['global'],
        error: error,
      })
      return this.next()
    case 'unknown_identifier':
      this.syntax.push({
        ...this.position(this.token),
        type: ['global'],
        error: 'invalid variable name',
      })
      return this.next()
    case 'special':
    case 'assign':
    case 'binary':
    case 'mixed':
    case 'unary':
    case 'delete':
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator'],
        error: error,
      })
      return this.next()
    case 'unknown_operator':
      this.syntax.push({
        ...this.position(this.token),
        type: ['operator'],
        error: 'unknown operator',
      })
      return this.next()
    case 'block':
      this.syntax.push({
        ...this.position(this.token),
        type: ['block'],
        error: error,
      })
      return this.next()
    case 'control':
      this.syntax.push({
        ...this.position(this.token),
        type: ['block'],
        error: error,
      })
      return this.next()
    case 'comment':
      this.syntax.push({
        ...this.position(this.token),
        type: ['comment'],
        error: error,
      })
      return this.next()
    case 'group':
      this.syntax.push({
        ...this.position(this.token),
        type: ['group'],
        error: error,
      })
      return this.next()
    case 'space':
    case 'newline':
      return this.next()
    case 'unknown_char':
      // eslint-disable-next-line no-redeclare
      var begin = this.token, length = 0
      while (this.token && this.token.type === 'unknown_char') {
        length += this.token.text.length
        this.next()
      }
      this.syntax.push({
        ...this.position(begin, length),
        type: ['error'],
        error: error || 'unexpected character',
      })
      return
    default:
      this.syntax.push({
        ...this.position(this.token),
        type: ['error'],
        error: error || 'unexpected thing',
      })
      return this.next()
    }
  }
}

export { Parser }
